import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import MeritCard from "@/components/MeritCard";
import { CloudDivider, SectionTitle } from "@/components/common";
import { HeroGuanyinLotusBackdrop } from "@/components/HeroGuanyinLotusBackdrop";
import { HandHeart, Flower2, Flower, ArrowRight } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] } }),
};

export default function Welcome() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [promoted, setPromoted] = useState([]);

  useEffect(() => {
    api.get("/projects", { params: { promoted: true } }).then((r) => setPromoted(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const features = [
    { icon: Flower, title: t("welcome.feature1Title"), desc: t("welcome.feature1Desc"), to: user ? "/dashboard" : "/member" },
    { icon: HandHeart, title: t("welcome.feature2Title"), desc: t("welcome.feature2Desc"), to: "/projects" },
    { icon: Flower2, title: t("welcome.feature3Title"), desc: t("welcome.feature3Desc"), to: "/lotus" },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <HeroGuanyinLotusBackdrop />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-52 sm:pb-72 text-center">
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="font-display text-4xl sm:text-6xl font-bold text-[hsl(var(--brand-ink))] drop-shadow-[0_2px_10px_hsla(var(--brand-paper),0.6)]">
            {lang === "zh" ? "欢迎到来" : "Welcome"}
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="mt-3 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto">
            {t("tagline")}
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3}
            className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="lg" data-testid="welcome-start-button"
              className="bg-[hsl(var(--primary))] text-primary-foreground px-8 shadow-lg"
              onClick={() => navigate("/daily")}>
              {t("welcome.cta")} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button size="lg" variant="outline" className="border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-paper)/0.75)] backdrop-blur-sm px-8 shadow-sm"
              data-testid="welcome-chart-button"
              onClick={() => navigate(user ? "/dashboard" : "/member")}>
              {t("nav.dashboard")}
            </Button>
          </motion.div>
          <p className="mt-3 text-xs text-muted-foreground">{t("welcome.ctaSub")}</p>
        </div>
      </section>

      <CloudDivider />

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}>
              <Card className="card-lift cursor-pointer h-full border-border bg-card" onClick={() => navigate(f.to)} data-testid={`welcome-feature-${i}`}>
                <CardContent className="p-6">
                  <div className="h-12 w-12 rounded-xl bg-[hsl(var(--brand-gold-soft))] flex items-center justify-center">
                    <f.icon className="h-6 w-6 text-[hsl(var(--brand-bronze))]" />
                  </div>
                  <h3 className="mt-4 font-display text-xl font-semibold">{f.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-6">{f.desc}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Promoted projects */}
      {promoted.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <SectionTitle zh="精选功德项目" en="Featured Merit Projects" lang={lang} kicker="FEATURED" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {promoted.map((p) => <MeritCard key={p.id} project={p} />)}
          </div>
        </section>
      )}

      {/* Values */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="rounded-2xl bg-[hsl(var(--primary))] text-primary-foreground px-6 py-12 text-center">
          <Flower2 className="h-8 w-8 mx-auto text-[hsl(var(--brand-gold-soft))]" />
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold">{t("welcome.valuesTitle")}</h2>
          <p className="mt-3 text-sm sm:text-base text-primary-foreground/80 max-w-2xl mx-auto">
            {lang === "zh"
              ? "每一份参与，都将化作实际的帮助，送到需要的人手中。消费 RM1 即可获得 0.01 功德点数。"
              : "Every contribution becomes real help delivered to those in need. Spend RM1 to earn 0.01 merit points."}
          </p>
        </div>
      </section>
    </div>
  );
}
